import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
    PasskeysResponse,
    RegisterData,
    RegisterResponse,
    ForgotPasswordData,
    ForgotPasswordResponse,
    ResetPasswordData,
    ResetPasswordResponse,
    ResendVerificationData,
    ResendVerificationResponse,
    VerifyEmailResponse,
} from '../types';

export function useRegister() {
    return useMutation({
        mutationFn: (data: RegisterData) =>
            api.post<RegisterResponse>('/api/register', {
                email: data.email,
                password: data.password,
                fax_number: data.faxNumber,
            }),
    });
}

export function usePasskeys() {
    return useQuery({
        queryKey: ['passkeys'],
        queryFn: () => api.get<PasskeysResponse>('/api/account/passkeys'),
    });
}

export function useDeletePasskey() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete<void>(`/api/account/passkeys/${id}`),
        onSuccess: (_, deletedId) => {
            queryClient.setQueryData<PasskeysResponse>(['passkeys'], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    passkeys: old.passkeys.filter((passkey) => passkey.id !== deletedId),
                };
            });
        },
    });
}

export function useRefreshPasskeys() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    };
}

export function useForgotPassword() {
    return useMutation({
        mutationFn: (data: ForgotPasswordData) =>
            api.post<ForgotPasswordResponse>('/api/forgot-password', {
                email: data.email,
                fax_number: data.faxNumber,
            }),
    });
}

export function useResetPassword() {
    return useMutation({
        mutationFn: (data: ResetPasswordData) =>
            api.post<ResetPasswordResponse>('/api/reset-password', {
                token: data.token,
                password: data.password,
                fax_number: data.faxNumber,
            }),
    });
}

export function useVerifyEmail() {
    return useMutation({
        mutationFn: (token: string) =>
            api.get<VerifyEmailResponse>(`/api/verify-email?token=${encodeURIComponent(token)}`),
    });
}

export function useResendVerification() {
    return useMutation({
        mutationFn: (data: ResendVerificationData) =>
            api.post<ResendVerificationResponse>('/api/resend-verification', data),
    });
}
